import { useState } from "react";
import { ArrowDown, ArrowUp, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BLANK = { time: "", title: "", description: "", display_order: 0 };

export function RunningOrderEditor({ eventId }) {
  const { data, loading, refetch } = useGet(`/staff/events/${eventId}/running-order`);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const entries = data || [];

  const open = (entry) => {
    setEditing(entry || null);
    setForm(entry ? { ...entry } : { ...BLANK, display_order: entries.length });
  };
  const save = async () => {
    if (!form.time || !form.title.trim()) {
      toast.error("Time and title are required.");
      return;
    }
    try {
      if (editing) {
        await api.put(`/staff/events/${eventId}/running-order/${editing.id}`, form);
      } else {
        await api.post(`/staff/events/${eventId}/running-order`, form);
      }
      setEditing(undefined);
      refetch();
      toast.success("Running order updated.");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const remove = async (entry) => {
    try {
      await api.delete(`/staff/events/${eventId}/running-order/${entry.id}`);
      refetch();
      toast.success("Running order entry removed.");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const move = async (index, direction) => {
    const reordered = [...entries];
    const destination = index + direction;
    if (destination < 0 || destination >= reordered.length) return;
    [reordered[index], reordered[destination]] = [reordered[destination], reordered[index]];
    try {
      await api.put(`/staff/events/${eventId}/running-order/reorder`, {
        entry_ids: reordered.map((entry) => entry.id),
      });
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  return (
    <section data-testid="staff-running-order-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Clients can view this event timeline but cannot edit it.</p>
        <Button className="rounded-full" onClick={() => open(null)} data-testid="add-running-order-entry">
          <Plus size={16} className="mr-1" /> Add timeline item
        </Button>
      </div>
      {loading ? <p className="mt-6 text-sm text-muted-foreground">Loading timeline…</p> : (
        <ol className="mt-6 divide-y divide-border border border-border bg-card rounded-lg">
          {entries.map((entry, index) => (
            <li key={entry.id} className="flex items-start gap-3 p-4" data-testid={`staff-running-order-${entry.id}`}>
              <time className="w-16 shrink-0 font-heading text-lg text-primary">{entry.time}</time>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-primary">{entry.title}</p>
                {entry.description && <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>}
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => move(index, -1)} disabled={index === 0} data-testid={`move-up-${entry.id}`} className="p-2 text-muted-foreground disabled:opacity-30"><ArrowUp size={16} /></button>
                <button onClick={() => move(index, 1)} disabled={index === entries.length - 1} data-testid={`move-down-${entry.id}`} className="p-2 text-muted-foreground disabled:opacity-30"><ArrowDown size={16} /></button>
                <button onClick={() => open(entry)} data-testid={`edit-running-order-${entry.id}`} className="p-2 text-muted-foreground hover:text-primary"><PencilSimple size={16} /></button>
                <button onClick={() => remove(entry)} data-testid={`delete-running-order-${entry.id}`} className="p-2 text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
              </div>
            </li>
          ))}
          {entries.length === 0 && <li className="p-5 text-sm text-muted-foreground">No timeline items yet.</li>}
        </ol>
      )}
      <Dialog open={editing !== undefined} onOpenChange={(value) => !value && setEditing(undefined)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Add"} timeline item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="eyebrow">Time</Label><Input type="time" className="mt-2" value={form.time} onChange={(event) => setForm({ ...form, time: event.target.value })} data-testid="running-order-time-input" /></div>
            <div><Label className="eyebrow">Title</Label><Input className="mt-2" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} data-testid="running-order-title-input" /></div>
            <div><Label className="eyebrow">Description</Label><Textarea className="mt-2" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} data-testid="running-order-description-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditing(undefined)}>Cancel</Button><Button onClick={save} data-testid="save-running-order-entry">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}